import { css } from '@emotion/react'
import { Alert, PagedTable, TabItem, Tabs } from 'bold-ui'
import { usePagedTableProps } from 'components/table'
import {
  useAcompanhamentoPuerperioAtividadesColetivasQuery,
  useAcompanhamentoPuerperioVisitasDomiciliaresQuery,
  useAcompanhamentoUltimosContatosQuery,
  useCidadaoQuery,
  useDisponibilidadeDadosClinicosHistoricoQuery,
} from 'graphql/hooks.generated'
import { HistoricoPayload, PageParams, Sort, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import { GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO } from 'types/NivelCbo'

import { getUltimosContatosColumns } from '../getUltimosContatosColumns'
import { FILTROS_OUTRAS_CONSULTAS, getFiltrosAtividadeColetiva, UltimosContatosTabs } from '../model-acompanhamentos'
import { completeConsultasPuerperio } from '../puerperio/util-acompanhamentoPuerperio'
import { getFiltrosConsultasPorAcompanhamento } from '../util/util-acompanhamentos'
import { AcompanhamentoCard } from './AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from './AcompanhamentoHeaderLink'

interface UltimosContatosCardProps {
  cidadaoId: ID
  atendimentoId: ID
  tipoAcompanhamento: TipoAcompanhamentoEnum
  dataInicioCondicao: Date
  dataFimCondicao?: Date
  historicoPath: string
  isEscutaInicial: boolean
}

export function UltimosContatosCard(props: Readonly<UltimosContatosCardProps>) {
  const {
    cidadaoId,
    atendimentoId,
    tipoAcompanhamento,
    dataInicioCondicao,
    dataFimCondicao,
    historicoPath,
    isEscutaInicial,
  } = props

  const [selectedTab, setSelectedTab] = useState(UltimosContatosTabs.CONSULTAS)
  const [tableState, setTableState] = useState<{ pageParams: PageParams }>({
    pageParams: {
      size: 8,
      fetchPageInfo: true,
    },
  })

  const isAcompanhamentoPuerperio = tipoAcompanhamento === TipoAcompanhamentoEnum.PUERPERIO

  const filtrosDinamicos = getFiltrosConsultasPorAcompanhamento(tipoAcompanhamento)

  const { data: cidadaoData, loading: loadingCidadao } = useCidadaoQuery({
    fetchPolicy: 'cache-first',
    variables: { id: cidadaoId },
    skip: !cidadaoId,
  })

  const { data } = useDisponibilidadeDadosClinicosHistoricoQuery({
    skip: !isAcompanhamentoPuerperio,
  })
  const disponibilidadeDadosClinicosHistorico = data?.disponibilidadeDadosClinicosHistorico

  const { data: historicoData, loading: loadingConsultas } = useAcompanhamentoUltimosContatosQuery({
    variables: {
      input: {
        cidadaoId,
        somenteMeusRegistros: false,
        sortDirection: Sort.DESC,
        tiposRegistro: filtrosDinamicos.tiposRegistro,
        excludeEscutaOrPreAtendimentoFromAtendProf: atendimentoId,
        pageParams: tableState.pageParams,
        filtrosAvancados: {
          periodo: { startDate: dataInicioCondicao, endDate: dataFimCondicao },
          cod2002Cbos: [...GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO],
          exibirRegistrosInativos: false,
          codCiaps2: filtrosDinamicos.codCiaps2,
          codCids10: filtrosDinamicos.codCids10,
        },
      },
    },
    skip: !cidadaoId || !dataInicioCondicao,
    fetchPolicy: 'cache-and-network',
  })

  const { data: visitasDomiciliares, loading: loadingVisitas } = useAcompanhamentoPuerperioVisitasDomiciliaresQuery({
    variables: {
      input: {
        cidadaoId,
        dataInicioCondicao,
        dataFimCondicao,
        pageParams: tableState.pageParams,
      },
    },
    skip: !cidadaoId || !dataInicioCondicao,
  })

  const skipAtividadesColetivasQuery = !cidadaoId || !dataInicioCondicao

  const {
    data: { atividadesColetivas } = {},
    loading: loadingAtividadesColetivas,
  } = useAcompanhamentoPuerperioAtividadesColetivasQuery({
    variables: {
      input: {
        cidadaoId,
        periodo: {
          startDate: dataInicioCondicao,
          endDate: dataFimCondicao,
        },
        isProcessado: true,
        ...getFiltrosAtividadeColetiva(isAcompanhamentoPuerperio),
        pageParams: {
          ...tableState.pageParams,
          sort: ['-dataAtividade'],
        },
      },
      cidadaoId,
    },
    skip: skipAtividadesColetivasQuery,
  })

  const skipOutrasConsultasQuery = !cidadaoId || !dataInicioCondicao || !isAcompanhamentoPuerperio

  const {
    data: { historico: outrasConsultas } = {},
    loading: loadingOutrasConsultas,
  } = useAcompanhamentoUltimosContatosQuery({
    variables: {
      input: {
        cidadaoId,
        somenteMeusRegistros: false,
        sortDirection: Sort.DESC,
        tiposRegistro: FILTROS_OUTRAS_CONSULTAS.tiposRegistros,
        excludeEscutaOrPreAtendimentoFromAtendProf: atendimentoId,
        pageParams: tableState.pageParams,
        filtrosAvancados: {
          periodo: { startDate: dataInicioCondicao, endDate: dataFimCondicao },
          exibirRegistrosInativos: false,
        },
      },
    },
    skip: skipOutrasConsultasQuery,
    fetchPolicy: 'cache-and-network',
  })

  const consultas = useMemo(() => {
    if (!historicoData?.historico) return []

    if (!isAcompanhamentoPuerperio) {
      return historicoData.historico.content
    }

    return completeConsultasPuerperio(historicoData.historico as HistoricoPayload, dataInicioCondicao)
  }, [historicoData, isAcompanhamentoPuerperio, dataInicioCondicao])

  const consultasPuerperioCount = historicoData?.historico?.pageInfo?.totalElements
  const visitasDomiciliaresCount =
    visitasDomiciliares?.acompanhamentoPuerperioVisitasDomiciliares?.pageInfo?.totalElements
  const atividadesColetivasCount = atividadesColetivas?.pageInfo?.totalElements
  const outrasConsultasCount = outrasConsultas?.pageInfo?.totalElements

  const currentColumns = getUltimosContatosColumns(
    tipoAcompanhamento,
    dataInicioCondicao,
    cidadaoData?.cidadao?.dataNascimento
  )?.[selectedTab]

  const currentData = useMemo(() => {
    if (!dataInicioCondicao) return undefined

    if (selectedTab === UltimosContatosTabs.CONSULTAS) {
      return {
        content: consultas,
        pageInfo: historicoData?.historico?.pageInfo,
      }
    }

    if (selectedTab === UltimosContatosTabs.VISITAS_DOMICILIARES) {
      return {
        content: visitasDomiciliares?.acompanhamentoPuerperioVisitasDomiciliares?.content ?? [],
        pageInfo: visitasDomiciliares?.acompanhamentoPuerperioVisitasDomiciliares?.pageInfo,
      }
    }

    if (selectedTab === UltimosContatosTabs.ATIVIDADE_COLETIVA) {
      return {
        content: atividadesColetivas?.content ?? [],
        pageInfo: atividadesColetivas?.pageInfo,
      }
    }

    if (selectedTab === UltimosContatosTabs.OUTRAS_CONSULTAS) {
      return {
        content: outrasConsultas?.content ?? [],
        pageInfo: outrasConsultas?.pageInfo,
      }
    }
  }, [
    selectedTab,
    consultas,
    historicoData,
    visitasDomiciliares,
    atividadesColetivas,
    outrasConsultas,
    dataInicioCondicao,
  ])

  const loading =
    loadingConsultas || loadingVisitas || loadingCidadao || loadingAtividadesColetivas || loadingOutrasConsultas

  const tableProps = usePagedTableProps({
    result: currentData?.pageInfo ? currentData : undefined,
    loading,
    onChange: setTableState,
  })

  return (
    <AcompanhamentoCard
      id='ultimos-contatos'
      title='Últimos contatos'
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading}
      headerContent={
        !isEscutaInicial ? <AcompanhamentoHeaderLink title='Visualizar histórico' path={historicoPath} /> : undefined
      }
      justifyHeaderContent='space-between'
    >
      {!disponibilidadeDadosClinicosHistorico?.statusProcessamento && isAcompanhamentoPuerperio && (
        <Alert
          type='warning'
          styles={{
            wrapper: css`
              margin-bottom: 0.5rem;
            `,
          }}
        >
          Algumas consultas de puerpério podem não estar disponíveis no momento. Consulte o histórico para mais
          informações.
        </Alert>
      )}
      <Tabs style={styles.noBottomLine}>
        <TabItem
          active={selectedTab === UltimosContatosTabs.CONSULTAS}
          onClick={() => setSelectedTab(UltimosContatosTabs.CONSULTAS)}
        >
          {UltimosContatosTabs.CONSULTAS}
          {isAcompanhamentoPuerperio && ' de puerpério'}
          {` (${consultasPuerperioCount ?? 0})`}
        </TabItem>
        <TabItem
          active={selectedTab === UltimosContatosTabs.VISITAS_DOMICILIARES}
          onClick={() => setSelectedTab(UltimosContatosTabs.VISITAS_DOMICILIARES)}
        >
          {UltimosContatosTabs.VISITAS_DOMICILIARES}
          {` (${visitasDomiciliaresCount ?? 0})`}
        </TabItem>
        <TabItem
          active={selectedTab === UltimosContatosTabs.ATIVIDADE_COLETIVA}
          onClick={() => setSelectedTab(UltimosContatosTabs.ATIVIDADE_COLETIVA)}
        >
          {UltimosContatosTabs.ATIVIDADE_COLETIVA}
          {` (${atividadesColetivasCount ?? 0})`}
        </TabItem>

        {/* TODO (RoyalFlush): Implementar aba de Outras consultas também para o pré-natal #32961*/}
        {isAcompanhamentoPuerperio && (
          <TabItem
            active={selectedTab === UltimosContatosTabs.OUTRAS_CONSULTAS}
            onClick={() => setSelectedTab(UltimosContatosTabs.OUTRAS_CONSULTAS)}
          >
            {UltimosContatosTabs.OUTRAS_CONSULTAS}
            {` (${outrasConsultasCount ?? 0})`}
          </TabItem>
        )}
      </Tabs>
      <PagedTable columns={currentColumns ?? []} {...tableProps} sizeOptions={[8]} />
    </AcompanhamentoCard>
  )
}

const styles = {
  noBottomLine: css`
    border-bottom: 0;
  `,
}
